/**
 * Event Time Utilities (12-hour AM/PM <-> 24-hour HH:mm)
 * Preserves Asia/Kolkata timezone and database storage format.
 */

export const HOURS_12 = [
  "01", "02", "03", "04", "05", "06",
  "07", "08", "09", "10", "11", "12"
];

export const MINUTES_60 = Array.from({ length: 60 }, (_, i) =>
  String(i).padStart(2, "0")
);

/**
 * Parse any time string ("HH:mm", "HH:mm AM/PM", "H:mm AM/PM", etc.) into 12-hour components
 * @param {string} timeStr - e.g. "19:00", "10:00 AM", "01:30 PM", "00:00"
 * @returns {{ hour: string, minute: string, ampm: "AM"|"PM" }}
 */
export const parseTimeTo12Hour = (timeStr) => {
  if (!timeStr || typeof timeStr !== "string") {
    return { hour: "10", minute: "00", ampm: "AM" };
  }

  const trimmed = timeStr.trim();
  const ampmMatch = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (ampmMatch) {
    let h = parseInt(ampmMatch[1], 10);
    const m = parseInt(ampmMatch[2], 10);
    const meridian = ampmMatch[3]?.toUpperCase();

    if (meridian === "AM" || meridian === "PM") {
      if (h === 0) h = 12;
      return {
        hour: String(h).padStart(2, "0"),
        minute: String(m).padStart(2, "0"),
        ampm: meridian
      };
    } else {
      // 24-hour string like "19:00", "00:00", "12:00", "09:30"
      const isPM = h >= 12;
      let h12 = h % 12;
      if (h12 === 0) h12 = 12;
      return {
        hour: String(h12).padStart(2, "0"),
        minute: String(m).padStart(2, "0"),
        ampm: isPM ? "PM" : "AM"
      };
    }
  }

  return { hour: "10", minute: "00", ampm: "AM" };
};

/**
 * Convert 12-hour time components to backend-compatible 24-hour "HH:mm"
 * Handled cases:
 * - 12:00 AM = Midnight (00:00)
 * - 12:00 PM = Noon (12:00)
 * - 01:00 PM = 13:00
 * - 11:30 PM = 23:30
 * @param {string|number} hour - "01" - "12"
 * @param {string|number} minute - "00" - "59"
 * @param {"AM"|"PM"} ampm - "AM" or "PM"
 * @returns {string} 24-hour time "HH:mm"
 */
export const format12To24 = (hour, minute, ampm) => {
  let h = parseInt(hour, 10);
  const m = parseInt(minute, 10) || 0;
  const meridian = (ampm || "AM").toUpperCase();

  if (meridian === "AM") {
    if (h === 12) h = 0; // Midnight (12:00 AM = 00:00)
  } else if (meridian === "PM") {
    if (h < 12) h += 12; // Afternoon/Evening (01:00 PM = 13:00, 12:00 PM = 12:00)
  }

  const hh = String(h).padStart(2, "0");
  const mm = String(m).padStart(2, "0");
  return `${hh}:${mm}`;
};

/**
 * Format any time string into 12-hour format with AM/PM for display
 * e.g. "10:00" -> "10:00 AM"
 * e.g. "13:00" -> "01:00 PM"
 * e.g. "00:00" -> "12:00 AM"
 * @param {string} timeStr
 * @returns {string} e.g. "10:00 AM"
 */
export const formatDisplay12Hour = (timeStr) => {
  if (!timeStr) return "";
  const { hour, minute, ampm } = parseTimeTo12Hour(timeStr);
  return `${hour}:${minute} ${ampm}`;
};

/**
 * Calculate duration in minutes between start and end times in 24-hour format
 * Handles events ending at midnight (e.g. 11:00 PM to 12:00 AM) or next day
 * @param {string} start24 - "HH:mm"
 * @param {string} end24 - "HH:mm"
 * @returns {number} duration in minutes
 */
export const calculateDurationInMinutes = (start24, end24) => {
  if (!start24 || !end24) return 60;
  const [sh, sm] = start24.split(":").map(Number);
  const [eh, em] = end24.split(":").map(Number);

  const startMinutes = sh * 60 + sm;
  let endMinutes = eh * 60 + em;

  if (endMinutes <= startMinutes) {
    // Overnight / midnight boundary
    endMinutes += 24 * 60;
  }

  return endMinutes - startMinutes;
};
